import React from "react";
import { observer } from "mobx-react-lite";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { ScienceIcon, SendIcon, VisibilityIcon } from "../../icons";
import { useRootStore } from "../../store/contexts/RootStoreContext";
import { AutomationStore } from "./AutomationStore";

/** Causas típicas de una falla de ENVÍO a firma (pre-firma) y qué revisar. */
const CAUSES = [
  "Proveedor de firma no disponible o credenciales/API key inválidas (revisar la configuración de e-firma del proveedor).",
  "Documento faltante o inválido al preparar el envío (p.ej. partitura o PDF ausente).",
  "Corte de red transitorio con el servicio de firma — reintentar suele bastar.",
];

/**
 * Card de intervención para una edición PRE-firma cuyo envío a firma falló
 * (documentationState = signatureSendingFailed). A diferencia de la card
 * DNDA/SADAIC (post-firma, sobre SignedEdition), acá el recovery es una única
 * transición de reenvío y el "por qué" viene de documentationLastOperationalError.
 */
export const SignatureFailedCard = observer(
  ({ store, edition }: { store: AutomationStore; edition: any }) => {
    const { router } = useRootStore();
    const name = edition?.name ?? edition.id;
    const error = edition?.documentationLastOperationalError as string | undefined;
    const busy = store.resolving === edition.id;

    const confirmAndRetry = () => {
      const summary = [
        `Reintentar Envío de Firmas — "${name}"`,
        "",
        "Rearma los documentos y los reenvía al servicio de firma.",
        "Verificá primero la causa de abajo (proveedor/credenciales/documentos).",
        "",
        "¿Continuar?",
      ].join("\n");
      if (window.confirm(summary)) {
        void store.retrySendForSignature(edition);
      }
    };

    return (
      <Card variant="outlined" sx={{ borderColor: "error.main", borderWidth: 2 }}>
        <CardContent>
          {/* Header */}
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="h3">{name}</Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace", color: "error.main", fontWeight: 700 }}>
              Envío a firma: falló
            </Typography>
          </Stack>

          {/* Error operacional concreto persistido en la edición. */}
          {error && (
            <Alert severity="error" sx={{ py: 0.5, mt: 1.5 }}>
              {error}
            </Alert>
          )}

          {/* Guía de resolución */}
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
              Causas típicas y cómo resolver
            </Typography>
            <Stack spacing={0.5}>
              {CAUSES.map((c, i) => (
                <Typography key={i} variant="body2">
                  • {c}
                </Typography>
              ))}
            </Stack>
          </Box>

          {/* Acciones */}
          <Divider sx={{ my: 1.5 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              disabled={busy}
              onClick={confirmAndRetry}
            >
              {busy ? "…" : "Reintentar Envío de Firmas"}
            </Button>
            <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={() => router.goTo(`/edition/view/${edition.id}`)}>
              Ver edición
            </Button>
            <Button variant="outlined" startIcon={<ScienceIcon />} onClick={() => router.goTo(`/simulation?editionId=${edition.id}`)}>
              Ver en simulación
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }
);
